<main id="main-content" class="iq-flow" [class.test-active]="stage() === 'questions'">
  @if (stage() === 'language') {
    <section dsCard class="flow-card language-card" aria-labelledby="language-title">
      <p class="ds-eyebrow">{{ t('iq.welcomeEyebrow') }}</p>
      <h1 id="language-title">{{ t('iq.languageTitle') }}</h1>
      <p class="flow-lede">{{ t('iq.languageText') }}</p>
      <ds-field [label]="t('common.language')" forId="test-language">
        <select
          dsSelect
          id="test-language"
          [value]="selectedLanguage()"
          [disabled]="busy()"
          (change)="selectLanguage($any($event.target).value)"
        >
          @for (item of languages(); track item.code) {
            <option [value]="item.code" [selected]="item.code === selectedLanguage()">
              {{ item.nativeName }} — {{ item.name }}
            </option>
          }
        </select>
      </ds-field>
      <div class="language-facts" aria-label="Assessment details">
        <span><strong>40</strong>{{ t('iq.questionsLabel') }}</span>
        <span><strong>35</strong>{{ t('iq.minutesLabel') }}</span>
        <span><strong>4</strong>{{ t('iq.domainsLabel') }}</span>
      </div>
      <button
        dsButton
        size="large"
        type="button"
        class="issue-button"
        [disabled]="busy()"
        (click)="beginTest()"
      >
        {{ t('iq.startAssessment') }}<ds-icon name="arrow-right" />
      </button>
      <a dsButton variant="quiet" class="guide-entry-link" href="/en/content/"
        >Learn about IQ before you begin<ds-icon name="arrow-right"
      /></a>
    </section>
  }

  @if (stage() === 'loading') {
    <ds-state kind="loading" [title]="t('iq.loading')" [message]="t('iq.loadingText')" />
  }

  @if (stage() === 'error') {
    <ds-state kind="error" [title]="errorMessage()" [message]="t('iq.error')">
      <button dsButton type="button" (click)="startTest()">{{ t('iq.retry') }}</button>
    </ds-state>
  }

  @if (stage() === 'questions' && currentQuestion(); as question) {
    <div class="test-session-bar" aria-label="Assessment progress">
      <span>{{ session()?.answeredCount ?? 0 }}/{{ question.total }} {{ t('iq.answered') }}</span>
      <ds-assessment-progress
        [answered]="session()?.answeredCount ?? 0"
        [total]="question.total"
        [label]="t('iq.assessment')"
      />
      <ds-assessment-timer [seconds]="secondsRemaining()" [label]="t('iq.timeRemaining')" />
    </div>
    <section class="assessment-layout" [attr.aria-label]="t('iq.assessment')">
      <ds-question-card
        [domain]="
          domainLabel(question.domain) +
          ' ' +
          t('iq.reasoning') +
          ' · ' +
          t('iq.question') +
          ' ' +
          (question.position + 1) +
          ' ' +
          t('iq.of') +
          ' ' +
          question.total
        "
        [position]="question.position + 1"
        [total]="question.total"
        [prompt]="question.prompt"
        headingId="question-title"
        [class.question-entering]="questionEntering()"
        [attr.aria-busy]="busy() || savingAnswer()"
      >
        @for (option of question.options; track option; let optionIndex = $index) {
          <ds-answer-option
            [optionKey]="['A', 'B', 'C', 'D'][optionIndex]"
            [label]="option"
            [selected]="question.selectedOption === optionIndex"
            [tabStop]="question.selectedOption === null && optionIndex === 0"
            [disabled]="busy()"
            (chosen)="selectAnswer(question, optionIndex)"
          />
        }
      </ds-question-card>
      <ds-question-navigator
        [position]="question.position + 1"
        [total]="question.total"
        [previousDisabled]="question.position === 0 || busy()"
        [nextDisabled]="busy()"
        [previousLabel]="t('iq.previous')"
        [nextLabel]="question.position === question.total - 1 ? t('iq.finish') : t('iq.next')"
        [ofLabel]="t('iq.of')"
        (previous)="move(-1)"
        (next)="move(1)"
      />
      <p class="secure-note"><ds-icon name="check" />{{ t('iq.saved') }}</p>
    </section>
  }

  @if (stage() === 'identity') {
    <section dsCard class="flow-card identity-card" aria-labelledby="identity-title">
      <p class="ds-eyebrow">{{ t('iq.identityEyebrow') }}</p>
      <h1 id="identity-title">{{ t('iq.identityTitle') }}</h1>
      <p class="flow-lede">{{ t('iq.identityText') }}</p>
      <div class="identity-grid">
        <ds-field [label]="t('iq.firstNameLabel')" forId="first-name">
          <input
            dsInput
            id="first-name"
            type="text"
            maxlength="80"
            autocomplete="given-name"
            [placeholder]="t('iq.firstNamePlaceholder')"
            [value]="firstName()"
            [attr.aria-invalid]="identityError() ? 'true' : 'false'"
            [attr.aria-describedby]="identityError() ? 'identity-error' : null"
            (input)="updateIdentity('firstName', $any($event.target).value)"
          />
        </ds-field>
        <ds-field [label]="t('iq.lastNameLabel')" forId="last-name">
          <input
            dsInput
            id="last-name"
            type="text"
            maxlength="80"
            autocomplete="family-name"
            [placeholder]="t('iq.lastNamePlaceholder')"
            [value]="lastName()"
            [attr.aria-invalid]="identityError() ? 'true' : 'false'"
            [attr.aria-describedby]="identityError() ? 'identity-error' : null"
            (input)="updateIdentity('lastName', $any($event.target).value)"
          />
        </ds-field>
        <ds-field class="date-field" [label]="t('iq.dateOfBirthLabel')" forId="date-of-birth">
          <input
            dsInput
            id="date-of-birth"
            type="date"
            autocomplete="bday"
            [max]="maximumDateOfBirth()"
            [value]="dateOfBirth()"
            [attr.aria-invalid]="identityError() ? 'true' : 'false'"
            [attr.aria-describedby]="identityError() ? 'identity-error' : null"
            (input)="updateIdentity('dateOfBirth', $any($event.target).value)"
          />
        </ds-field>
      </div>
      @if (identityError()) {
        <p id="identity-error" class="field-error" role="alert">{{ identityError() }}</p>
      }
      <ds-alert [title]="t('iq.identityEyebrow')"
        ><p>{{ t('iq.nameNotice') }}</p></ds-alert
      >
      <button
        dsButton
        size="large"
        type="button"
        class="issue-button"
        [disabled]="busy()"
        (click)="finishTest()"
      >
        {{ t('iq.issueCertificate') }}<ds-icon name="arrow-right" />
      </button>
    </section>
  }

  @if (stage() === 'result' && result(); as score) {
    <section class="flow-card results" aria-labelledby="results-title">
      <div class="results-heading">
        <div>
          <p class="ds-eyebrow">{{ t('iq.resultEyebrow') }}</p>
          <h1 id="results-title">{{ t('iq.resultTitle') }}</h1>
          <p class="candidate-result-name">{{ candidateName() }}</p>
        </div>
        <ds-score-visualization
          [value]="score.correct * 2.5"
          [displayValue]="score.estimate"
          [label]="t('iq.estimate')"
        />
      </div>
      <ds-result-card
        [title]="bandLabel(score)"
        [score]="score.estimate"
        [range]="t('iq.range') + ' ' + score.rangeLow + '–' + score.rangeHigh"
        [summary]="score.correct + '/' + score.total + ' ' + t('iq.correct')"
      >
        <div class="domain-results">
          @for (domain of score.domains; track domain.domain) {
            <ds-progress
              [value]="domain.correct * 10"
              [label]="domainLabel(domain.domain) + ' ' + domain.correct + '/' + domain.total"
            />
          }
        </div>
      </ds-result-card>
      <section dsCard class="result-dashboard" aria-labelledby="result-dashboard-title">
        <header>
          <p class="ds-eyebrow">{{ t('iq.dashboardEyebrow') }}</p>
          <h2 id="result-dashboard-title">{{ t('iq.resultDashboardTitle') }}</h2>
        </header>
        <dl class="result-metrics">
          <div>
            <dt>{{ t('iq.percentileLabel') }}</dt>
            <dd>{{ percentile() }}th</dd>
            <p>{{ t('iq.percentileContext') }}</p>
          </div>
          <div>
            <dt>{{ t('iq.precisionLabel') }}</dt>
            <dd>{{ score.rangeLow }}&ndash;{{ score.rangeHigh }}</dd>
            <p>{{ t('iq.precisionContext') }}</p>
          </div>
          <div>
            <dt>{{ t('iq.strongestLabel') }}</dt>
            <dd>{{ strongestDomain(score) }}</dd>
            <p>{{ t('iq.focusLabel') }}: {{ focusDomain(score) }}</p>
          </div>
        </dl>
        <div class="domain-profile">
          <h3>{{ t('iq.domainProfileTitle') }}</h3>
          <div class="domain-profile-grid">
            @for (domain of score.domains; track domain.domain) {
              <article>
                <span>{{ domainLabel(domain.domain) }}</span
                ><strong>{{ domainAccuracy(domain.correct, domain.total) }}%</strong
                ><small>{{ domainStatus(domain.correct, domain.total) }}</small>
              </article>
            }
          </div>
        </div>
        <div class="practice-plan">
          <div>
            <h3>{{ t('iq.practicePlanTitle') }}</h3>
            <p>{{ t('iq.practicePlanText') }}</p>
          </div>
          <nav [attr.aria-label]="t('iq.practicePlanTitle')">
            @for (domain of score.domains; track domain.domain) {
              <a [href]="practiceUrl(domain.domain)"
                >{{ domainLabel(domain.domain) }} {{ t('iq.reviewGuide') }}</a
              >
            }
          </nav>
        </div>
      </section>
      <ds-alert title="{{ t('iq.explanationTitle') }}"
        ><p>{{ t('iq.explanation') }}</p></ds-alert
      >
      <section dsCard class="answer-review" aria-labelledby="answer-review-title">
        <header class="answer-review-heading">
          <div>
            <p class="ds-eyebrow">{{ t('iq.answerReviewEyebrow') }}</p>
            <h2 id="answer-review-title">{{ t('iq.answerReviewTitle') }}</h2>
            <p>{{ t('iq.answerReviewText') }}</p>
          </div>
          <div class="answer-review-totals" [attr.aria-label]="t('iq.answerReviewTitle')">
            <span class="correct-total"
              ><strong>{{ score.correct }}</strong> {{ t('iq.correct') }}</span
            >
            <span class="incorrect-total"
              ><strong>{{ score.total - score.correct }}</strong> {{ t('iq.incorrect') }}</span
            >
          </div>
        </header>
        <ol class="answer-review-list">
          @for (question of score.questions; track question.id) {
            <li
              class="answer-review-item"
              [class.is-correct]="question.isCorrect"
              [class.is-incorrect]="!question.isCorrect"
            >
              <div class="answer-review-question">
                <p>
                  {{ t('iq.question') }} {{ question.position }} {{ t('iq.of') }} {{ score.total }}
                  <span aria-hidden="true">·</span>
                  {{ domainLabel(question.domain) }}
                  @if (question.timeSpentSeconds) {
                    <span aria-hidden="true">&middot;</span>
                    {{ question.timeSpentSeconds }} {{ t('iq.timeSpent') }}
                  }
                </p>
                <h3>{{ question.prompt }}</h3>
              </div>
              <ds-badge [tone]="question.isCorrect ? 'success' : 'danger'">
                <ds-icon [name]="question.isCorrect ? 'check' : 'warning'" />
                {{ question.isCorrect ? t('iq.correctStatus') : t('iq.incorrectStatus') }}
              </ds-badge>
              <dl class="answer-review-answers">
                <div>
                  <dt>{{ t('iq.yourAnswer') }}</dt>
                  <dd>{{ question.selectedAnswer ?? t('iq.notAnswered') }}</dd>
                </div>
                <div class="correct-answer">
                  <dt>{{ t('iq.correctAnswer') }}</dt>
                  <dd>{{ question.correctAnswer }}</dd>
                </div>
              </dl>
            </li>
          }
        </ol>
      </section>
      <section dsCard class="email-result" aria-labelledby="email-result-title">
        <div>
          <p class="ds-eyebrow">Keep your result</p>
          <h2 id="email-result-title">Email the secure links to yourself</h2>
          <p>The message contains your private verification URL and certificate download link.</p>
        </div>
        @if (resultEmailSent()) {
          <ds-alert title="Email queued"
            ><p>
              The delivery worker will send your result shortly. Check your spam folder if it does
              not arrive.
            </p></ds-alert
          >
        } @else {
          <form (submit)="$event.preventDefault(); emailResult()">
            <label for="result-email">Email address</label>
            <div>
              <input
                dsInput
                id="result-email"
                type="email"
                autocomplete="email"
                [value]="resultEmail()"
                (input)="resultEmail.set($any($event.target).value); resultEmailError.set('')"
              /><button dsButton type="submit" [disabled]="busy()">Send result</button>
            </div>
            @if (resultEmailError()) {
              <p class="field-error" role="alert">{{ resultEmailError() }}</p>
            }
          </form>
        }
      </section>
      <section dsCard class="result-feedback" aria-labelledby="feedback-title">
        <div>
          <p class="ds-eyebrow">Help improve IQLabTest</p>
          <h2 id="feedback-title">How was this assessment experience?</h2>
          <p>
            Feedback is stored with this completed test ID. Do not include sensitive personal
            information.
          </p>
        </div>
        @if (feedbackSent()) {
          <ds-alert title="Thank you"><p>Your feedback has been recorded.</p></ds-alert>
        } @else {
          <form (submit)="$event.preventDefault(); submitFeedback()">
            <fieldset>
              <legend>Overall rating</legend>
              <div class="rating-options">
                @for (rating of [1, 2, 3, 4, 5]; track rating) {
                  <button
                    type="button"
                    [class.active]="feedbackRating() === rating"
                    [attr.aria-pressed]="feedbackRating() === rating"
                    (click)="feedbackRating.set(rating)"
                  >
                    {{ rating }}<span class="ds-visually-hidden"> out of 5</span>
                  </button>
                }
              </div>
            </fieldset>
            <label for="feedback-comment">Optional comment</label>
            <textarea
              id="feedback-comment"
              maxlength="500"
              rows="3"
              [value]="feedbackComment()"
              (input)="feedbackComment.set($any($event.target).value)"
            ></textarea>
            <button dsButton type="submit" [disabled]="feedbackRating() < 1 || feedbackBusy()">
              Send feedback
            </button>
            @if (feedbackError()) {
              <p class="field-error" role="alert">{{ feedbackError() }}</p>
            }
          </form>
        }
      </section>
      <div class="result-actions">
        <a
          dsButton
          size="large"
          [href]="certificateUrl()"
          (click)="trackResultAction('certificate_downloaded')"
          ><ds-icon name="download" />{{ t('iq.downloadCertificate') }}</a
        >
        <a
          dsButton
          variant="secondary"
          [href]="verificationUrl()"
          (click)="trackResultAction('verification_opened')"
          >{{ t('iq.verifyResult') }}</a
        >
        <button dsButton variant="quiet" type="button" (click)="chooseAnotherTest()">
          {{ t('iq.restart') }}
        </button>
      </div>
    </section>
  }
</main>
